import {
  ISurveyContract,
  ISurveyQuestion,
} from "../zeos-api/contracts/Survey";
import { QuestionTypeEnum } from "../zeos-api/enums/Survey";
import {
  flattenSurveyQuestions,
  isQuestionType,
  isQuestionVisible,
  isQuestionnaireVisible,
} from "./SurveyVisibilityHelpers";

export type OnboardingSummaryRow = {
  label: string;
  value: string;
};

export type SurveySummarySection = {
  title: string;
  items: Array<{
    question: { displayName: string };
    answerLabel: string;
  }>;
};

export const formatSurveyAnswerLabel = (
  question: ISurveyQuestion,
  value: unknown
): string | null => {
  if (value == null || value === "") {
    return null;
  }

  if (isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText)) {
    return String(value);
  }

  const selectedIds = Array.isArray(value)
    ? value.map((id) => Number(id))
    : [Number(value)];

  const labels = selectedIds
    .map((id, index) => {
      const opt = question.options.find(
        (o) => Number(o.responseOptionId) === id
      );
      if (!opt) {
        return null;
      }
      if (
        isQuestionType(question.questionTypeId, QuestionTypeEnum.RankedChoice)
      ) {
        const rank =
          index === 0
            ? "Primary"
            : index === 1
              ? "Secondary"
              : index === 2
                ? "Tertiary"
                : `Rank ${index + 1}`;
        return `${rank}: ${opt.displayName}`;
      }
      return opt.displayName;
    })
    .filter(Boolean) as string[];

  if (labels.length === 0) {
    return null;
  }

  return labels.join(", ");
};

export const buildOnboardingAnswerSummary = (
  template: ISurveyContract,
  answers: Record<number, any>,
  summaryLabels: string[]
): OnboardingSummaryRow[] => {
  const questions = flattenSurveyQuestions(template)
    .filter((q) => isQuestionVisible(q, answers))
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const rows: OnboardingSummaryRow[] = [];

  questions.forEach((question, index) => {
    const value = formatSurveyAnswerLabel(question, answers[question.questionId]);
    if (!value) {
      return;
    }
    rows.push({
      label: summaryLabels[index] ?? question.displayName,
      value,
    });
  });

  return rows;
};

export const buildSurveySummarySections = (
  template: ISurveyContract,
  answers: Record<number, any>
): SurveySummarySection[] => {
  return template.questionnaires
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((questionnaire) => {
      if (!isQuestionnaireVisible(questionnaire.visibilityRules, answers)) {
        return null;
      }
      const items = questionnaire.questions
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .filter((question) => isQuestionVisible(question, answers))
        .map((question) => {
          const answerLabel = formatSurveyAnswerLabel(
            question,
            answers[question.questionId]
          );
          if (!answerLabel) {
            return null;
          }
          return {
            question,
            answerLabel,
          };
        })
        .filter(Boolean) as SurveySummarySection["items"];

      if (!items.length) {
        return null;
      }

      return {
        title: questionnaire.displayName,
        items,
      };
    })
    .filter(Boolean) as SurveySummarySection[];
};
